/**
 * Chargement asynchrone avec états explicites.
 *
 * Chaque écran du back-office suit la même vie : chargement → données, ou chargement → erreur
 * avec une reprise possible. Ce hook évite de réécrire sept fois la même machine à états, et
 * garantit deux choses :
 * 1. une réponse arrivée après le démontage de l'écran ne provoque aucun avertissement React ;
 * 2. une nouvelle tentative repart d'un état propre (l'erreur précédente est effacée).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { versErreurApi } from '../api/erreurs';

import type { ErreurApi } from '../api/erreurs';

export interface EtatChargement<T> {
  readonly donnees: T | null;
  readonly erreur: ErreurApi | null;
  readonly chargement: boolean;
  readonly recharger: () => void;
}

/**
 * Exécute `charge` au montage, puis à chaque `recharger()` ou changement de `dependances`.
 *
 * `charge` est relu à chaque rendu par une référence : l'appelant peut passer une fonction
 * fléchée sans provoquer de boucle de chargement. Les `dependances` servent aux écrans paginés,
 * où la page courante fait partie de la requête.
 */
export function useChargement<T>(
  charge: (signal: AbortSignal) => Promise<T>,
  dependances: readonly unknown[] = [],
): EtatChargement<T> {
  const [donnees, setDonnees] = useState<T | null>(null);
  const [erreur, setErreur] = useState<ErreurApi | null>(null);
  const [chargement, setChargement] = useState(true);
  const [version, setVersion] = useState(0);

  const chargeRef = useRef(charge);
  chargeRef.current = charge;

  const recharger = useCallback(() => {
    setVersion((precedente) => precedente + 1);
  }, []);

  useEffect(() => {
    const controleur = new AbortController();
    let actif = true;
    setChargement(true);
    setErreur(null);

    chargeRef
      .current(controleur.signal)
      .then((resultat) => {
        if (!actif) return;
        setDonnees(resultat);
        setChargement(false);
      })
      .catch((inconnu: unknown) => {
        if (!actif) return;
        setErreur(versErreurApi(inconnu));
        setChargement(false);
      });

    return () => {
      actif = false;
      controleur.abort();
    };
    // `version` porte les rechargements explicites ; `dependances` les paramètres de requête
    // (page courante, filtre serveur). Les deux concourent au même cycle de chargement.
  }, [version, ...dependances]);

  return { donnees, erreur, chargement, recharger };
}
